import React, {useCallback, useEffect} from 'react';
import {Stack} from '@chakra-ui/react';
import {useDispatch, useSelector} from 'react-redux';
import {useSwiper} from 'swiper/react';
import {UiText, UiBox, UiInput, UiFormControl, UiFormErrorMessage, Col} from '~/components/elements';
import CustomRadio from '~/components/elements/CustomRadio';
import {
  DATA_CHECK_BOX,
  NO_OPTION,
  POP_UP_PH_CHECKOUT_KEYS,
  QUESTIONS,
  QUESTIONS_PARAMS,
  YES_OPTION,
} from '~/constants/popup';
import {useInput} from '~/hooks';
import {RootState} from '~/redux/root-reducer';
import {MedicalQuestionnaireKey} from '~/redux/questionnaires/types';
import {BackAndNextSlide, PopupLayout, TitlePopup} from '~/pages/homes/pop-up/components';
import {setQuestionnaireAnswer} from '~/redux/questionnaires/slice';

export const PopupTwo = () => {
  const dispatch = useDispatch();
  const swiper = useSwiper();
  const slideKey = useSelector((state: RootState) => state.questionnaires.nextSlideKey);
  const answer = useSelector((state: RootState) => state.questionnaires.collectData);
  const isGoback = !!answer[slideKey as MedicalQuestionnaireKey];

  const questionnaire = answer?.[POP_UP_PH_CHECKOUT_KEYS.CURRENTLY_MEDICATIONS];
  const {data: answerData} = questionnaire || {};

  const defaultValue = {
    selectedOption: answerData?.selectedOption || '',
    treatments: answerData?.treatments || '',
  };

  const rules = {
    treatments: function () {
      if (inputs.selectedOption === YES_OPTION) {
        return {
          presence: {allowEmpty: false, message: `^This field can't be blank`},
          length: {maximum: 300, message: `^This field is too long (maximum is 300 characters)`},
        };
      } else {
        return {presence: false};
      }
    },
  };

  const {inputs, setInputs, validation} = useInput(defaultValue, {rules});

  const isNextSlide =
    inputs.selectedOption === YES_OPTION
      ? Object.values(inputs).every(el => !!el)
      : inputs.selectedOption === NO_OPTION;

  const handleNextSlide = useCallback(() => {
    if (validation.getErrors()) return;
    const data = inputs.selectedOption === YES_OPTION ? inputs : {selectedOption: NO_OPTION};

    dispatch(
      setQuestionnaireAnswer({
        key: POP_UP_PH_CHECKOUT_KEYS.CURRENTLY_MEDICATIONS,
        index: 'SCREEN 2',
        data,
        questions: QUESTIONS_PARAMS.CURRENTLY_MEDICATIONS,
      }),
    );
    setTimeout(() => {
      swiper.allowSlideNext = true;
      swiper.slideNext();
    }, 200);
  }, [dispatch, inputs, swiper, validation]);

  useEffect(() => {
    if (slideKey === POP_UP_PH_CHECKOUT_KEYS.CURRENTLY_MEDICATIONS) {
      if (isNextSlide) {
        if (validation?.errors?.treatments || inputs.selectedOption === YES_OPTION) {
          swiper.allowSlideNext = false;
          return;
        }
        swiper.allowSlideNext = true;
        if (isGoback) return;
        if (inputs.selectedOption === NO_OPTION) {
          handleNextSlide();
        }
      } else {
        swiper.allowSlideNext = false;
      }
    }
  }, [handleNextSlide, inputs.selectedOption, isGoback, isNextSlide, slideKey, swiper, validation?.errors?.treatments]);

  const handleClickOptions = (value: string) => {
    setInputs({selectedOption: value});
  };

  return (
    <>
      <BackAndNextSlide onNextSlide={handleNextSlide} isActiveNextSlide={isNextSlide} />
      <PopupLayout>
        <TitlePopup>{QUESTIONS.CURRENTLY_MEDICATIONS.selectedOption}</TitlePopup>
        <Stack direction="row" my={4}>
          {DATA_CHECK_BOX.map(val => (
            <CustomRadio
              style={{marginRight: 40, cursor: 'pointer'}}
              key={val.label}
              text={val.label}
              checked={inputs.selectedOption === val.label}
              onClick={() => handleClickOptions(val.label)}
            />
          ))}
        </Stack>
        {inputs.selectedOption === YES_OPTION && (
          <UiFormControl id="treatments" isRequired isInvalid={!!validation?.errors?.treatments}>
            <UiBox>
              <TitlePopup>{QUESTIONS.CURRENTLY_MEDICATIONS.treatments}</TitlePopup>
              <UiBox>
                <UiInput
                  value={inputs.treatments}
                  height="48px"
                  borderRadius="8px"
                  onChange={({target}) => setInputs({treatments: target.value})}
                />
              </UiBox>
              {validation?.errors?.treatments && (
                <UiFormErrorMessage px={1}>{validation?.errors?.treatments[0]}</UiFormErrorMessage>
              )}
            </UiBox>
          </UiFormControl>
        )}
      </PopupLayout>
    </>
  );
};
